// vue
import { ref } from 'vue';

// exchanges methods
import { useExchanges } from './exchanges';

// shared
import { getStartTime } from './startTime';

// chart interface
import type { EntityInfo, IChartingLibraryWidget } from 'charting_library';

// types
import { Props } from '../../../types';

// store
import { useGl } from '@store/ts/gl';

// useComputed
import { useComputed } from './computed';

// useGlobals
import { useGlobals } from './globals';

// useOrders
import { useOrders } from './orders';

export function useChart(props: Props) {
    // store
    const gl = useGl();

    // vars
    const getBarsLoading = ref(false);

    // generation token to prevent stale async callbacks after re-init/unmount/HMR
    const tvGen = ref(0);

    // useExchanges
    const { getFetch } = useExchanges(props);

    // useGlobals
    const {
        init,
        widget,
        barsGl,
        interval,
        timeFrame,
        widgetOrders,
        chartContainer,
        historyOrdersQueue,
    } = useGlobals(props);

    // useOrders
    const { renderOrders } = useOrders(props);

    // useComputed
    const { pair, overrides, customThemes, exchangeFullInfo } = useComputed(props);

    // основная конфигурация графика
    const chartConfig = (container: HTMLElement | null, pairSymbol: string) => {
        return {
            container,
            library_path:
        window.location.origin === 'https://app.revenuebot.io'
            ? '/office/charting_library/'
            : '/charting_library/',
            symbol: pairSymbol,
            interval: timeFrame.value,
            locale: 'ru',
            autosize: true,
            theme: gl.darkTheme ? 'dark' : 'light',
            custom_themes: customThemes,
            custom_css_url: '../css/chart-styles.css',
            disabled_features: [
                'items_favoriting',
                'show_object_tree',
                'timeframes_toolbar',
                'timezone_menu',
                gl.isMobile ? 'left_toolbar' : null,
            ],
        };
    };

    // onReady
    const onReady = (callback: (config: any) => void) => {
        callback({
            supports_search: true,
            supports_group_request: false,
            supports_marks: false,
            supports_timescale_marks: false,
        });
    };

    // определение кол-ва знаков после запятой для цена на графике
    const getNumberOfDecimalPlaces = async (
        pairSymbol: string,
        exchangeName: string,
    ): Promise<number> => {
        const bars = await getFetch(exchangeFullInfo.value)({
            symbol: pairSymbol,
            interval: '1h',
            from: {
                from: getStartTime('1h'),
                to: Date.now(),
            },
            limit: 1,
        });

        const trimmed = parseFloat(bars[0].open.toString());
        const decimalPlaces = trimmed.toString().includes('.')
            ? trimmed.toString().split('.')[1].length
            : 0;

        return decimalPlaces === 1 ? 100 : 10 ** decimalPlaces;
    };

    // resolveSymbol
    const resolveSymbol = async (
        symbolName: string,
        onSymbolResolvedCallback: (symbolInfo: any) => void,
        onResolveErrorCallback: (error: any) => void,
    ) => {
        try {
            const numberOfDecimalPlaces = await getNumberOfDecimalPlaces(
                pair.value,
                exchangeFullInfo.value.code_name,
            );

            // можно без таймера, но оставим небольшую асинхронность как было
            setTimeout(() => {
                const symbolInfo = {
                    name: symbolName,
                    full_name: symbolName,
                    description: symbolName,
                    type: 'crypto',
                    session: '24x7',
                    timezone: 'Etc/UTC',
                    minmov: 1,
                    pricescale: numberOfDecimalPlaces,
                    has_intraday: true,
                    has_daily: true,
                    has_weekly_and_monthly: true,
                    supported_resolutions: [
                        '1',
                        '3',
                        '5',
                        '15',
                        '30',
                        '45',
                        '60',
                        '120',
                        '240',
                        '1D',
                        '1W',
                        '1M',
                    ],
                };

                onSymbolResolvedCallback(symbolInfo);
            }, 50);
        } catch (e) {
            console.error('resolveSymbol error', e);
            onResolveErrorCallback(e);
        }
    };

    // getBars
    const getBars = async (
        symbolInfo: any,
        resolution: string,
        periodParams: any,
        onHistoryCallback: (bars: any[], meta?: any) => void,
        onErrorCallback: (error: any) => void,
    ) => {
        getBarsLoading.value = true;

        try {
            const bars = await getFetch(exchangeFullInfo.value)({
                symbol: symbolInfo.ticker,
                interval: resolution,
                from: periodParams,
            });

            // сохраняем бары глобально
            if (resolution !== timeFrame.value) {
                barsGl[resolution] = bars;
            } else {
                if (!Array.isArray(barsGl[resolution])) {
                    barsGl[resolution] = [];
                }
                barsGl[resolution].push(...bars);
            }

            if (bars.length === 0) {
                onHistoryCallback([], { noData: true });
            } else {
                onHistoryCallback(bars);
            }

            timeFrame.value = resolution;
        } catch (error) {
            console.error('Ошибка при получении данных:', error);
            onErrorCallback(error);
        }

        getBarsLoading.value = false;
    };

    // subscribeBars
    const subscribeBars = (
        symbolInfo: any,
        resolution: string,
        onRealtimeCallback: (bar: any) => void,
        onErrorCallback: (error: any) => void,
    ) => {
        if (interval.value) {
            clearInterval(interval.value);
        }

        const fetchNewBar = async () => {
            try {
                const bars = await getFetch(exchangeFullInfo.value)({
                    symbol: symbolInfo.ticker,
                    interval: resolution,
                    from: {
                        from: getStartTime(resolution),
                        to: Date.now(),
                    },
                    limit: 1,
                });

                if (bars.length > 0) {
                    const latestBar = bars[bars.length - 1];
                    onRealtimeCallback({
                        time: latestBar.time,
                        open: latestBar.open,
                        high: latestBar.high,
                        low: latestBar.low,
                        close: latestBar.close,
                        volume: latestBar.volume,
                    });
                }
            } catch (error) {
                console.error('Ошибка при получении новых данных:', error);
                onErrorCallback(error);
            }
        };

        interval.value = setInterval(async () => {
            await fetchNewBar();
        }, 3000);

        return {
            unsubscribe: () => {
                if (interval.value) clearInterval(interval.value);
            },
        };
    };

    // unsubscribeBars
    const unsubscribeBars = (subscriberData: any) => {
        if (subscriberData?.unsubscribe) {
            subscriberData.unsubscribe();
        }
    };

    // searchSymbols
    const searchSymbols = async (
        userInput: string,
        exchange: string,
        symbolType: string,
        onResultReadyCallback: (result: any) => void,
    ) => {
        onResultReadyCallback([]);
    };

    // подписка на получение новых данных
    const attachOnDataLoaded = (w: IChartingLibraryWidget, gen: number) => {
        w.onChartReady(() => {
            if (gen !== tvGen.value) return;

            const chart = w.chart?.();
            if (!chart) return;

            chart.onDataLoaded().subscribe(null, () => {
                if (gen !== tvGen.value) return;

                getBarsLoading.value = false;

                // Если ордеров ещё нет — отрисуем все
                if (!Object.keys(widgetOrders).length) {
                    renderOrders();
                } else if (historyOrdersQueue.value.length) {
                    // Если появились исторические — дорисуем их
                    renderOrders(true);
                }
            });
        });
    };

    // подписка на обновление таймфрейма
    const attachOnIntervalChanged = (w: IChartingLibraryWidget, gen: number) => {
        w.onChartReady(() => {
            if (gen !== tvGen.value) return;

            const chart = w.chart?.();
            if (!chart) return;

            chart.onIntervalChanged().subscribe(null, () => {
                if (gen !== tvGen.value) return;
                // resetwidgetOrders(); // при необходимости
            });
        });
    };

    // инициализация скриптов графика
    const initScripts = (): void => {
        const chartingLibraryScript = document.createElement('script');
        chartingLibraryScript.src = '/charting_library/charting_library.js';

        chartingLibraryScript.onload = () => {
            if (!window.TradingView) {
                console.error('Charting Library не загружена!');
                return;
            }

            const datafeedScript = document.createElement('script');
            datafeedScript.src = `${
                window.location.origin === 'https://app.revenuebot.io' ? '/office' : ''
            }/datafeeds/udf/dist/bundle.js`;

            datafeedScript.onload = () => {
                if (!window.Datafeeds) {
                    console.error('Datafeed не загружен!');
                    return;
                }
            };

            datafeedScript.onerror = () => {
                console.error('Не удалось загрузить Datafeed!');
            };

            document.body.appendChild(datafeedScript);
        };

        chartingLibraryScript.onerror = () => {
            console.error('Не удалось загрузить Charting Library!');
        };

        document.body.appendChild(chartingLibraryScript);
    };

    // инициализация графика
    const initChartLibraty = async (pairSymbol: string) => {
    // invalidate previous callbacks/subscriptions (stale protection)
        tvGen.value += 1;
        const gen = tvGen.value;

        init.value = false;

        try {
            // создаём конкретный экземпляр и фиксируем его в локальную переменную
            const w: IChartingLibraryWidget = new window.TradingView.widget({
                ...chartConfig(chartContainer.value, pairSymbol),
                datafeed: {
                    onReady,
                    resolveSymbol,
                    getBars,
                    subscribeBars,
                    unsubscribeBars,
                    searchSymbols,
                },
                timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
            });

            // сохраняем как “текущий” (но дальше работаем через w)
            widget.value = w;

            w.onChartReady(async () => {
                if (gen !== tvGen.value) return;

                // overrides
                try {
                    await w.applyOverrides(overrides.value);
                } catch (e) {
                    console.warn('applyOverrides failed', e);
                }

                // убрать Volume
                try {
                    const studies = w.chart().getAllStudies();
          studies?.forEach((study: EntityInfo) => {
              if (study.name.includes('Volume')) {
                  w.chart().removeEntity(study.id);
              }
          });
                } catch (e) {
                    console.warn('remove Volume study failed', e);
                }

                // подписки (без setTimeout)
                attachOnIntervalChanged(w, gen);
                attachOnDataLoaded(w, gen);

                // первичная отрисовка (все равно setOrder внутри должен быть safe)
                renderOrders(true);

                init.value = true;
            });
        } catch (e) {
            console.log(e);

            // retry только если это всё ещё актуальная попытка
            setTimeout(() => {
                if (gen !== tvGen.value) return;
                initChartLibraty(pairSymbol);
            }, 500);
        }
    };

    return {
        interval,
        getBarsLoading,
        getBars,
        onReady,
        chartConfig,
        initScripts,
        subscribeBars,
        resolveSymbol,
        searchSymbols,
        unsubscribeBars,
        initChartLibraty,
        getNumberOfDecimalPlaces,
    };
}